import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorPage } from '@/pages/public/ErrorPage';

interface Props {
  children: ReactNode;
  /** Override the fallback UI. Defaults to <ErrorPage />. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Telemetry hook — called once per caught error. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

/**
 * Top-level React error boundary.
 *
 * Catches synchronous render errors in the entire React tree and shows a
 * branded fallback instead of a blank page. React Router handles routing
 * errors via `errorElement`; this is the last-resort net underneath.
 *
 * Note: ErrorBoundaries do NOT catch async errors (use try/catch around
 * async work) or event-handler errors (use try/catch in the handler).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught render error:', error, info);
    this.props.onError?.(error, info);
    // In Phase 2: also POST to /functions/v1/log-client-error for server-side telemetry.
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);
      return <ErrorPage error={this.state.error} reset={this.reset} />;
    }
    return this.props.children;
  }
}
